import { Fragment, type ReactNode } from "react";

interface RenderArticleRichTextOptions {
  paragraphClassName?: string;
  bulletListClassName?: string;
  bulletItemClassName?: string;
  headingClassName?: string;
  standaloneBoldClassName?: string;
  spacerClassName?: string;
}

function renderInlineBold(text: string) {
  const parts = text.split(/(\*\*.*?\*\*)/g);

  return parts.filter(Boolean).map((part, index) => {
    const isBold = part.startsWith("**") && part.endsWith("**") && part.length > 4;

    if (!isBold) {
      return <Fragment key={index}>{part}</Fragment>;
    }

    return <strong key={index} className="font-bold">{part.slice(2, -2)}</strong>;
  });
}

export function renderArticleRichText(text: string, options: RenderArticleRichTextOptions = {}) {
  const {
    paragraphClassName = "text-sm leading-relaxed text-gray-900",
    bulletListClassName = "ml-5 list-disc space-y-2",
    bulletItemClassName = "text-sm leading-relaxed text-gray-900",
    headingClassName = "mt-8 mb-3 text-xl font-black text-gray-900",
    standaloneBoldClassName = "mt-4 mb-1 text-sm font-bold text-gray-900",
    spacerClassName = "h-2",
  } = options;

  const lines = text.split(/\r?\n/);
  const elements: ReactNode[] = [];
  let key = 0;
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();

    if (!line) {
      elements.push(<div key={`space-${key++}`} className={spacerClassName} />);
      index += 1;
      continue;
    }

    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={`heading-${key++}`} className={headingClassName}>
          {renderInlineBold(line.slice(3))}
        </h2>
      );
      index += 1;
      continue;
    }

    if (line.startsWith("- ")) {
      const items: string[] = [];

      while (index < lines.length && lines[index].trim().startsWith("- ")) {
        items.push(lines[index].trim().slice(2));
        index += 1;
      }

      elements.push(
        <ul key={`list-${key++}`} className={bulletListClassName}>
          {items.map((item, itemIndex) => (
            <li key={`item-${itemIndex}`} className={bulletItemClassName}>
              {renderInlineBold(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    const isStandaloneBold = line.startsWith("**") && line.endsWith("**") && line.length > 4 && !line.slice(2, -2).includes("**");

    if (isStandaloneBold) {
      elements.push(
        <p key={`bold-${key++}`} className={standaloneBoldClassName}>
          {line.slice(2, -2)}
        </p>
      );
      index += 1;
      continue;
    }

    elements.push(
      <p key={`paragraph-${key++}`} className={paragraphClassName}>
        {renderInlineBold(line)}
      </p>
    );
    index += 1;
  }

  return elements;
}
